if (!customElements.get('quiz-bundle-builder')) {
  customElements.define('quiz-bundle-builder', class QuizBundleBuilder extends HTMLElement {
    #loaderTimeoutId = null;
    #modalStepsResizeObserver = null;

    constructor() {
      super();
      const poolEl = this.querySelector('#QuizPool');
      this.pool = poolEl ? JSON.parse(poolEl.textContent) : [];
      this.errorDisplay = this.querySelector('#QuizError');

      // Initialize state with arrays for all selections
      this.state = {
        currentStep: 1,
        kidsCount: 0,
        selectedAges: [],
        selectedInterests: []
      };
    }

    connectedCallback() {
      this.initEvents();
      this.#initModalStableStepHeight();
    }

    disconnectedCallback() {
      this.#clearResultsLoaderTimeout();
      this.#modalStepsResizeObserver?.disconnect();
      this.#modalStepsResizeObserver = null;
    }

    /**
     * Initialize all click event listeners
     */
    initEvents() {
      this.addEventListener('click', (e) => {
        const t = e.target;

        // Standard button navigation (closest: clicks on inner nodes still hit the button)
        if (t.closest?.('.next-btn')) this.moveStep(1);
        if (t.closest?.('.prev-btn')) this.moveStep(-1);
        if (t.closest?.('.submit-btn')) this.calculate();
        if (t.closest?.('.reset-btn')) this.reset();

        // Unified Card Selection
        const card = t.closest('.interest-card');
        if (card) {
          const type = card.dataset.type;

          if (type === 'kids-count') {
            this.selectKidsCount(card);
          } else if (type === 'age-selection') {
            this.toggleAgeSelection(card);
          } else if (type === 'activity-selection') {
            this.toggleDummySelection(card);
          } else {
            // Interest cards (they have data-collection-id)
            this.toggleInterest(card);
          }
        }
      });

      this.addEventListener('keydown', (e) => {
        const t = e.target;
        const card = t?.closest?.('.interest-card');
        if (!card) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });
    }

    // Step 1: Single selection (Radio behavior)
    selectKidsCount(card) {
      this.querySelectorAll('[data-type="kids-count"]').forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
      this.state.kidsCount = parseInt(card.dataset.value);
      this.hideError();

      // Update Step 2 Title based on count
      const ageTitle = this.querySelector('.quiz-age-title');
      if (ageTitle) {
        ageTitle.textContent = this.state.kidsCount === 1
          ? ageTitle.dataset.titleSingular
          : ageTitle.dataset.titlePlural;
      }
    }

    // Step 2: Multi selection (Checkbox behavior)
    toggleAgeSelection(card) {
      const val = parseInt(card.dataset.value);
      const idx = this.state.selectedAges.indexOf(val);

      if (idx > -1) {
        this.state.selectedAges.splice(idx, 1);
        card.classList.remove('is-selected');
        card.setAttribute('aria-checked', 'false');
      } else {
        // Limit selection to the number of kids chosen in Step 1
        if (this.state.selectedAges.length < this.state.kidsCount) {
          this.state.selectedAges.push(val);
          card.classList.add('is-selected');
          card.setAttribute('aria-checked', 'true');
        } else {
          // Optional: remove oldest and add new
          const removedVal = this.state.selectedAges.shift();
          this.querySelector(`[data-type="age-selection"][data-value="${removedVal}"]`).classList.remove('is-selected');

          this.state.selectedAges.push(val);
          card.classList.add('is-selected');
          card.setAttribute('aria-checked', 'true');
        }
      }
      this.hideError();
    }

    /**
     * Toggles interest selection and manages state array
     * @param {HTMLElement} card
     */
    toggleInterest(card) {
      const id = card.dataset.collectionId;
      const idx = this.state.selectedInterests.indexOf(id);

      if (idx > -1) {
        this.state.selectedInterests.splice(idx, 1);
        card.classList.remove('is-selected');
      } else {
        this.state.selectedInterests.push(id);
        card.classList.add('is-selected');
      }
      card.setAttribute('aria-checked', card.classList.contains('is-selected'));
      this.hideError();
    }

    /**
     * Dummy multi-select (UI-only). Doesn't affect quiz state or results.
     * @param {HTMLElement} card
     */
    toggleDummySelection(card) {
      card.classList.toggle('is-selected');
      card.setAttribute('aria-checked', card.classList.contains('is-selected'));
      this.hideError();
    }

    /**
     * Directional step navigation with validation
     * @param {number} dir
     */
    moveStep(dir) {
      if (dir > 0 && !this.validate()) return;
      if (dir < 0 && this.state.currentStep <= this.#getMinQuizStepIndex()) return;
      this.state.currentStep += dir;
      this.updateUI();
    }

    #prefersReducedMotion() {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    }

    #readCssPxNumber(el, prop) {
      const v = window.getComputedStyle(el).getPropertyValue(prop).trim();
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    }

    /**
     * Scrolls viewport (or modal surface) to the top of the quiz section.
     */
    #scrollToSectionTop() {
      const section = this.closest('section.section-custom-quiz');
      if (!section) return;

      const behavior = this.#prefersReducedMotion() ? 'auto' : 'smooth';
      const modalContent = this.closest('.modal__content');

      // If quiz is inside a modal, scroll the modal surface instead of the page.
      if (modalContent && typeof modalContent.scrollTo === 'function') {
        const modalRect = modalContent.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const top = modalContent.scrollTop + (sectionRect.top - modalRect.top);
        modalContent.scrollTo({ top: Math.max(0, top), behavior });
        return;
      }

      const root = document.documentElement;
      const headerOffset =
        this.#readCssPxNumber(root, '--header-height') * this.#readCssPxNumber(root, '--enable-sticky-header')
        + this.#readCssPxNumber(root, '--announcement-bar-height') * this.#readCssPxNumber(root, '--enable-sticky-announcement-bar');

      const targetTop = window.scrollY + section.getBoundingClientRect().top - headerOffset - 12;
      window.scrollTo({ top: Math.max(0, targetTop), behavior });
    }

    /**
     * Smallest step index among question / interstitial steps (excludes loader and results).
     * @returns {number}
     */
    #getMinQuizStepIndex() {
      let minIdx = Infinity;

      for (const el of this.querySelectorAll('.quiz-step[data-step-index]')) {
        if (el.classList.contains('quiz-result-step') || el.classList.contains('quiz-step--loader')) continue;
        const n = parseInt(el.dataset.stepIndex, 10);

        if (!Number.isNaN(n)) minIdx = Math.min(minIdx, n);
      }

      return Number.isFinite(minIdx) ? minIdx : 1;
    }

    /**
     * Validates current step before moving forward
     * @returns {boolean}
     */
    validate() {
      this.hideError();
      const currentStepEl = this.querySelector(`.quiz-step[data-step-index="${this.state.currentStep}"]`);
      if (!currentStepEl) return true;

      if (currentStepEl.classList.contains('quiz-step--interstitial') || currentStepEl.classList.contains('quiz-step--loader')) {
        return true;
      }

      // Activities step is a dummy step: never blocks navigation.
      if (currentStepEl.querySelector('[data-type="activity-selection"]')) {
        return true;
      }

      // Get the error message from the builder data or use a fallback
      const errorMsg = this.getAttribute('data-error-text') || "Please make a selection";

      // Validate Step 1: Kids Count
      if (currentStepEl.querySelector('[data-type="kids-count"]')) {
        if (this.state.kidsCount === 0) {
          this.showError(errorMsg);
          return false;
        }
      }

      // Validate Step 2: Ages
      if (currentStepEl.querySelector('[data-type="age-selection"]')) {
        if (this.state.selectedAges.length === 0) {
          this.showError(errorMsg);
          return false;
        }
      }

      // Validate Step 3: Interests
      if (currentStepEl.querySelector('.interest-grid') && !currentStepEl.querySelector('[data-type]')) {
        if (this.state.selectedInterests.length === 0) {
          this.showError(errorMsg);
          return false;
        }
      }

      return true;
    }

    /**
     * Syncs the visual UI with the current state
     */
    updateUI() {
      const currentIdx = this.state.currentStep;

      this.querySelectorAll('.quiz-step').forEach(s => {
        const idx = parseInt(s.dataset.stepIndex, 10);
        s.classList.toggle('is-active', idx === currentIdx);
      });

      const stepEl = this.querySelector(`.quiz-step[data-step-index="${currentIdx}"]`);
      const progressStep = stepEl?.dataset.progressStep;

      this.querySelectorAll('.step-indicator').forEach(ind => {
        const s = parseInt(ind.dataset.step, 10);

        if (progressStep === 'loader' || progressStep === 'results') {
          ind.classList.remove('is-active');
          ind.classList.add('is-completed');
          return;
        }

        if (progressStep) {
          const p = parseInt(progressStep, 10);

          if (!Number.isNaN(p)) {
            ind.classList.toggle('is-active', s === p);
            ind.classList.toggle('is-completed', s < p);
          }

          return;
        }

        ind.classList.toggle('is-active', s === currentIdx);
        ind.classList.toggle('is-completed', s < currentIdx);
      });

      const minStepIdx = this.#getMinQuizStepIndex();

      for (const btn of this.querySelectorAll('.prev-btn')) {
        btn.hidden = currentIdx <= minStepIdx;
      }

      this.errorDisplay.classList.remove('is-visible');
      this.#syncModalStableStepHeight();
      this.#scrollToSectionTop();
    }

    /**
     * In modal mode, lock quiz-form height to the tallest question/interstitial step
     * so switching steps does not shift the popup layout.
     */
    #initModalStableStepHeight() {
      const modal = this.closest('.quiz-bundle-modal');
      const form = this.querySelector('.quiz-form');

      if (!modal || !form) return;

      form.classList.add('quiz-form--modal-stable-height');

      this.#syncModalStableStepHeight();

      if (!this.#modalStepsResizeObserver) {
        this.#modalStepsResizeObserver = new ResizeObserver(() => {
          this.#syncModalStableStepHeight();
        });
        this.#modalStepsResizeObserver.observe(form);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.#syncModalStableStepHeight());
      }

      for (const img of form.querySelectorAll('img')) {
        if (img.complete) continue;
        img.addEventListener('load', () => this.#syncModalStableStepHeight(), { once: true });
      }
    }

    #getModalMeasurableSteps() {
      const form = this.querySelector('.quiz-form');

      if (!form) return [];

      return [...form.querySelectorAll('.quiz-step')].filter(
        (step) => !step.classList.contains('quiz-result-step') && !step.classList.contains('quiz-step--loader')
      );
    }

    #syncModalStableStepHeight() {
      const form = this.querySelector('.quiz-form');

      if (!form?.classList.contains('quiz-form--modal-stable-height')) return;

      const currentStepEl = this.querySelector(`.quiz-step[data-step-index="${this.state.currentStep}"]`);

      if (
        currentStepEl?.classList.contains('quiz-result-step')
        || currentStepEl?.classList.contains('quiz-step--loader')
      ) {
        form.style.removeProperty('--quiz-modal-steps-min-height');
        return;
      }

      const steps = this.#getModalMeasurableSteps();

      if (!steps.length) return;

      form.classList.add('quiz-form--measuring');

      let maxHeight = 0;

      for (const step of steps) {
        maxHeight = Math.max(maxHeight, step.offsetHeight);
      }

      form.classList.remove('quiz-form--measuring');

      if (maxHeight > 0) {
        form.style.setProperty('--quiz-modal-steps-min-height', `${maxHeight}px`);
      }
    }

    /**
     * Calculates the final product distribution for the bundle.
     * Logic: Distributes the total number of children across products that match
     * both the selected interests and the age criteria.
     */
    calculate() {
      if (!this.validate()) return;

      const selectedAges = this.state.selectedAges;
      const totalKids = this.state.kidsCount;
      const interests = this.state.selectedInterests.map(id => Number(id));

      // Initialize bundle with the main product (e.g., Pen).
      // Default: add exactly 1. Optional: match kids count.
      const mainProductQty = this.dataset.mainProductPerKid === 'true' ? totalKids : 1;
      let bundle = { [this.dataset.mainProductId]: mainProductQty };

      this.pool.forEach(p => {
        // 1. Check if the product matches any of the selected interest collections
        const hasInterestMatch = p.collection_ids.some(id => interests.includes(Number(id)));

        if (hasInterestMatch) {
          // 2. Extract supported ages from product tags (e.g., tags containing "ages-info_X")
          const productAgeTags = p.tags
            .filter(t => t.includes('ages-info_'))
            .map(t => parseInt(t.split('_')[1]));

          // 3. Check if at least one selected age is compatible with this product
          const hasAgeMatch = selectedAges.some(a => productAgeTags.includes(a));

          if (hasAgeMatch) {
            // Pool products are always added with quantity 1
            bundle[p.id] = 1;
          }
        }
      });

      const params = Object.entries(bundle)
        .map(([id, q]) => `${id}%3A${q}`)
        .join('%2C');

      const finalUrl = `${this.dataset.baseUrl}?bundle=${params}&redirect_from_c_quiz=true`;
      // Check if we should show results step or redirect immediately
      if (this.dataset.showResults === 'true') {
        this.renderResultStep(bundle, finalUrl);
        const loaderStep = this.querySelector('.quiz-step--loader');

        if (loaderStep) {
          const loaderIdx = parseInt(loaderStep.dataset.stepIndex, 10);
          this.state.currentStep = loaderIdx;
          this.updateUI();
          this.#scheduleResultsLoaderTransition();
        } else {
          const resultStep = this.querySelector('.quiz-result-step');

          if (resultStep) {
            this.state.currentStep = parseInt(resultStep.dataset.stepIndex, 10);
            this.updateUI();
          }
        }
      } else {
        window.location.href = finalUrl;
      }
    }

    /**
     * Updates the visibility and quantities in the Result Step
     */
    renderResultStep(bundle, finalUrl) {
      const resultStep = this.querySelector('.quiz-result-step');
      if (!resultStep) return;

      // Set the final link to the button
      const finalBtn = resultStep.querySelector('.final-bundle-link');
      if (finalBtn) finalBtn.href = finalUrl;

      // Hide all items first
      resultStep.querySelectorAll('.quiz-result-item').forEach(item => {
        item.style.display = 'none';
        item.classList.remove('is-visible');
      });

      // Show only items present in the bundle and update their quantity badges
      Object.entries(bundle).forEach(([id, qty]) => {
        const itemEl = resultStep.querySelector(`[data-product-id="${id}"]`);
        if (itemEl) {
          itemEl.style.display = 'block';
          itemEl.classList.add('is-visible');

          // Handle quantity badge visibility
          const qtyBadge = itemEl.querySelector('.product-qty-badge');
          if (qtyBadge) {
            if (qty === 1) {
              // Hide badge when quantity is 1
              qtyBadge.style.display = 'none';
            } else {
              qtyBadge.style.display = '';
              const qtyLabel = qtyBadge.querySelector('.qty-val');
              if (qtyLabel) qtyLabel.textContent = qty;
            }
          }
        }
      });
    }

    /**
     * Resets the quiz to the first step without page reload
     */
    reset() {
      this.#clearResultsLoaderTimeout();
      this.state = { currentStep: 1, kidsCount: 0, selectedAges: [], selectedInterests: [] };
      this.querySelectorAll('.interest-card').forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      this.updateUI();
    }

    #clearResultsLoaderTimeout() {
      if (this.#loaderTimeoutId != null) {
        clearTimeout(this.#loaderTimeoutId);
        this.#loaderTimeoutId = null;
      }
    }

    #scheduleResultsLoaderTransition() {
      this.#clearResultsLoaderTimeout();
      const loaderStep = this.querySelector('.quiz-step--loader');

      if (!loaderStep) return;

      const raw = loaderStep.dataset.loaderDurationMs;
      const parsed = raw ? parseInt(raw, 10) : 5000;
      const ms = Number.isNaN(parsed) || parsed < 0 ? 5000 : parsed;

      this.#loaderTimeoutId = setTimeout(() => {
        this.#loaderTimeoutId = null;
        const resultStep = this.querySelector('.quiz-result-step');

        if (resultStep) {
          this.state.currentStep = parseInt(resultStep.dataset.stepIndex, 10);
          this.updateUI();
        }
      }, ms);
    }

    showError(m) { this.errorDisplay.textContent = m; this.errorDisplay.classList.add('is-visible'); }
    hideError() { this.errorDisplay.classList.remove('is-visible'); }
  });
}